import { property, supertypeClass } from '@haventech/amorphic';
import { ApplicationOriginator } from '../ApplicationOriginator';
import { DuplicateApplicationOptions } from '../../../coverpath-agent/public/js/controllers/AccountCenterController';
import { Policy } from '../../../common/js/customer/Policy';
import { Utils } from '../../../common/js/Utils';
import { QuoteRider } from '../../../common/js/quote/QuoteRider';
import { BaseQuote } from '../../../common/js/quote/BaseQuote';
import { Agent } from '../../../common/js/customer/Agent';
import { AgentProxy } from '../../../common/js/agent/AgentProxy';
import { OpsProxy } from '../../../common/js/agent/OpsProxy';
import { Entitlement } from '../../../common/js/agent/UserPrincipal';
import { Application, ApplicationSourceType } from '../../../common/js/customer/Application';
import { CoverpathPolicy } from './CoverpathPolicy';
import { ApplicationTypes, FeatureFlags, OriginatorActions, Partners } from '../../js/interfaces/CoverpathTypes';
import { QuoteExchange1035 } from '../../../common/js/quote/coverpath/QuoteExchange1035';
import { CoverpathTermQuote } from '../quote/coverpath/CoverpathTermQuote';
import { CoverpathWholeLifeQuote } from '../quote/coverpath/CoverpathWholeLifeQuote';
import { CoverpathAnnuityQuote } from '../quote/coverpath/CoverpathAnnuityQuote';
import { CoverpathBasePolicy } from './CoverpathBasePolicy';
import _ from 'lodash';
import { Product } from '../../../common/js/static/Product';
import { CoverpathBaseApplication } from './CoverpathBaseApplication';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { LaunchDarklyInstance, LD_APP } from '../../../common/js/launch-darkly/LaunchDarklySingleton';
import { POLICY_TYPES } from '../../../common/js/constants/policy';
import { SourceOfFunds } from '../../../common/js/customer/SourceOfFunds';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class CasApplication extends CoverpathBaseApplication {
  @property()
  agentEsignComplete: boolean = false;

  @property()
  agentEsignCompleteDateUw: Date;

  // Property is true when an advisor assisted application is in the process of being sent, after a link of another type has already been sent
  @property({
    comment:
      'Property is true when an advisor assisted application is in the process of being sent, after a link of another type has already been sent',
  })
  linkBeingResent: boolean = false;

  @property()
  farthestFullySeen: number;

  @property({ comment: 'Progress percentage of the application' })
  progressPercentage: number = 0;

  @property({ values: ApplicationTypes })
  type: string;

  @property({ comment: 'Who created the app' })
  applicationOriginator: ApplicationOriginator;

  @property({ comment: 'Author of records that are related to this application', getType: () => ApplicationOriginator })
  actionOriginators: Array<ApplicationOriginator>;

  @property({ getType: () => SourceOfFunds })
  sourceOfFunds: SourceOfFunds;

  @property({
    comment: 'foreign key id of current app_user editing application',
    toServer: false,
  })
  lockedBy: string;

  @property({
    comment: 'indicates wether a case qualifies for Society 1851 benefits',
    toServer: false,
  })
  isSociety1851: boolean = false;

  @property({
    comment: 'indicates if generation of a docusign url link is in progress',
    toServer: false,
  })
  docuSignLinkInProgress: boolean = false;

  @property({
    comment:
      'tracks the partner who created the application - this is similar to partnerName on Customer table except ' +
      "this column tracks it per application. CP will not use Customer's partnerName.",
    toServer: false,
  })
  partnerName: Partners;

  @property({
    comment: 'this is set when the preview offer is accepted',
  })
  previewOfferAcceptedDate: Date;

  @property({ comment: 'this is set when the preview offer is sent to the customer' })
  previewOfferSentDate: Date;

  @property({ type: Array, getType: () => CoverpathPolicy })
  policies: Array<CoverpathPolicy> = [];

  @property({
    isLocal: true,
  })
  get isTeleCMILanguageEnglish(): boolean {
    return this.teleCMILanguage === 'English';
  }

  set isTeleCMILanguageEnglish(value: boolean) {
    this.teleCMILanguage = value ? 'English' : '';
  }

  @property({
    comment: 'Language for Tele-CMI',
  })
  teleCMILanguage: string = 'English';

  @property({
    comment: 'Mode of Contact for Tele-CMI',
  })
  teleCMIModeOfContact: string = 'email';

  @property({
    comment: 'Reason for Tele-CMI',
  })
  teleCMIReason: string;

  @property({
    comment: 'Reason for Tele-CMI - other',
  })
  teleCMIReasonOther: string;

  @property({
    comment: 'Best time to call for Tele-CMI',
  })
  teleCMIBestTimeToCall: string;

  @property({
    isLocal: true,
  })
  get tlicOptedIn(): boolean {
    return !this.skipTLIC;
  }

  set tlicOptedIn(value: boolean) {
    this.skipTLIC = !value;
  }

  @property({ comment: 'Indicates if the advisor wants to skip evaluating TLIC eligibility for this application' })
  skipTLIC: boolean = false;

  @property({
    isLocal: true,
  })
  get manualExam(): boolean {
    return this.examOptOut;
  }

  set manualExam(value: boolean) {
    this.examOptOut = value;
  }

  @property({
    comment: 'If selected, Coverpath orders a medical exam (when required) after the application is submitted.',
  })
  examOptOut: boolean = false;

  @property({
    isLocal: true,
  })
  get isLockedByAdvisor(): boolean {
    return this.lockedByAdvisor;
  }

  set isLockedByAdvisor(value: boolean) {
    this.lockedByAdvisor = value;
  }

  static new(customer) {
    return new CasApplication().init(customer);
  }

  init(customer) {
    super.init(customer);
    this.applicationOriginator = ApplicationOriginator.new(this);
    this.sourceOfFunds = SourceOfFunds.new(this);
    return this;
  }

  isCasApplication(): this is CasApplication {
    return this.constructor.name === 'CasApplication';
  }

  addPolicy(policy: CoverpathPolicy) {
    super.addPolicy(policy as CoverpathBasePolicy);
  }

  addSecondaryPolicy(policy: CoverpathPolicy) {
    super.addSecondaryPolicy(policy as CoverpathBasePolicy);
  }

  getTlicTotal() {
    let amount = this.primaryPolicy.tLICPremiumBreakdown.getTotal();
    if (this.isJointApp()) {
      amount += this.secondaryPolicy.tLICPremiumBreakdown.getTotal();
    }
    return amount.toFixed(2);
  }

  isAgentEsignPending(): boolean {
    return !this.agentEsignComplete;
  }

  /**
   * preserve the "is this an agent assisted application" flag
   * @param oldApplication, isAppSubmitted
   * @returns {CasApplication}
   */
  async cloneApplicationProps(oldApplication: CasApplication, isAppSubmitted: boolean): Promise<CasApplication> {
    this.isAgentAssistant = oldApplication.isAgentAssistant;
    this.progressPercentage = isAppSubmitted ? oldApplication.progressPercentage : 0;
    this.quote = oldApplication.quote;
    this.lockedBy = oldApplication.lockedBy;

    this.preserveApplicationProps(oldApplication, this);

    if (
      !CoverpathUtils.isSanderlingPolicy(oldApplication.primaryPolicy.product) &&
      oldApplication.applicationOriginator.originatorId
    ) {
      const originatorRole = oldApplication.applicationOriginator.originatorType;
      this.setApplicationOriginatorProxyId(oldApplication.applicationOriginator.originatorId, originatorRole);
    }

    return this;
  }

  /**
   * preserve the teleCMI and source of funds properties
   * @param oldApplication, newApplication
   */
  async preserveApplicationProps(oldApplication: CasApplication, newApplication: CasApplication): Promise<void> {
    let isTeleCMIEnabled: boolean = false;
    let tlicOptionalityEnabled: boolean = false;
    let examOptOutEnabled: boolean = false;

    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    if (ldInstance) {
      isTeleCMIEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
        FeatureFlags.isTeleCMIEnabled,
        oldApplication.primaryPolicy.servicingAgent.producerId
      );
      tlicOptionalityEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
        FeatureFlags.tlicOptionalityEnabled,
        oldApplication.primaryPolicy.servicingAgent.producerId
      );
      examOptOutEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
        FeatureFlags.examOptOutEnabled,
        oldApplication.primaryPolicy.servicingAgent.producerId
      );
    }
    if (isTeleCMIEnabled) {
      newApplication.isTeleCMI = oldApplication.primaryPolicy.isTeleCMIRequired();
      newApplication.teleCMILanguage = oldApplication.teleCMILanguage;
      newApplication.teleCMIModeOfContact = oldApplication.teleCMIModeOfContact;
      newApplication.teleCMIBestTimeToCall = oldApplication.teleCMIBestTimeToCall;
      newApplication.teleCMIReason = oldApplication.teleCMIReason;
      newApplication.teleCMIReasonOther = oldApplication.teleCMIReasonOther;
    }

    newApplication.sourceOfFunds = SourceOfFunds.new(newApplication);
    if (oldApplication.sourceOfFunds) {
      oldApplication.sourceOfFunds.duplicateToNewSOF(newApplication.sourceOfFunds);
    }

    newApplication.skipTLIC = tlicOptionalityEnabled ? oldApplication.skipTLIC : false;
    if (
      examOptOutEnabled &&
      (oldApplication.primaryPolicy.convertedFrom === null ||
        oldApplication.primaryPolicy.convertedFrom === undefined) &&
      oldApplication.examOptOut !== null &&
      oldApplication.examOptOut !== undefined
    ) {
      newApplication.examOptOut = oldApplication.examOptOut;
    } else {
      newApplication.examOptOut = false;
    }
    newApplication.lockedByAdvisor = oldApplication.lockedByAdvisor;
  }

  /**
   * Create a new application by cloning the original application.
   * An application has three children: policy, quote and applicationOriginator.
   * Policy(ies) and quote(s) are cloned. applicationOriginator is created anew at Application.new().
   * Different from application.createClone() because:
   * - quote and its three relationships are also cloned (secondaryQuote, quoteRiders, quoteExchange1035)
   * - original application is not marked as prior
   * - controller.customer.application (_currentApplication) remains the same (not swapped out with the new application)
   */
  async duplicate(
    duplicateApplicationOptions?: DuplicateApplicationOptions,
    isRevertingToDraft?: boolean
  ): Promise<CasApplication> {
    const filteredPolicies = this.policies.filter(
      (policy) => policy.type === POLICY_TYPES.PRIMARY || policy.type === POLICY_TYPES.SECONDARY
    );
    const sourceType = isRevertingToDraft ? ApplicationSourceType.ADVISOR_EDIT : ApplicationSourceType.DUPLICATE;

    // STEP 1 - Create a new app
    const newApp = CasApplication.new(this.customer);

    // STEP 2 - Clone the policies and add to the new app.
    // This is the exact same clone done in submitAAApplication, cloneApplicationForReapply, cloneForConversion
    const clonedPolicies = await Promise.all<CoverpathPolicy>(
      filteredPolicies.map<Promise<CoverpathPolicy>>(
        (policy) => policy.createClonedPolicy(true, sourceType) as Promise<CoverpathPolicy>
      )
    );

    clonedPolicies.forEach(async (policy) => {
      await this.clonePremiumBreakdown(policy);
      policy.application = newApp;
    });
    newApp.policies = clonedPolicies;

    // STEP 3 - Clone quote(s) and add to the new app.
    // The clone includes cloning quote's three children: quoteExchange1035, quoteRider, and secondaryQuote.
    // STEP 3.1 clone quote and give it a new invite link (unless reverting to draft)
    const newQuote = this.cloneQuote(this.quote);
    if (isRevertingToDraft && this.quote.invite) {
      newQuote.invite = this.quote.invite;
    } else {
      newQuote.invite = `${Utils.generateUuid()}`;
    }
    newQuote.isAgentAssistant = true;
    newQuote.isAdvisorQuote = true;
    newQuote.application = newApp;
    newApp.quote = newQuote;

    // STEP 3.2 clone secondaryQuote if any (but only if secondary policy was not removed)
    if (this.quote.secondaryQuote && this.secondaryPolicy) {
      const newSecondaryQuote = this.cloneQuote(this.quote.secondaryQuote);
      newSecondaryQuote.isAgentAssistant = true;
      newSecondaryQuote.isAdvisorQuote = true;
      newSecondaryQuote.application = newApp;
      newQuote.secondaryQuote = newSecondaryQuote;
    }

    // STEP 3.3 clone quote riders if any
    this.quote.riders.forEach((rider) => {
      const newRider: QuoteRider = QuoteRider.new(rider.type, newQuote);
      newRider.amount = rider.amount;
      newRider.premiumWaiver = rider.premiumWaiver;
      newRider.premiumBase = rider.premiumBase;
      newRider.plannedYears = rider.plannedYears;
      newRider.paymentFrequency = rider.paymentFrequency;
      newQuote.riders.push(newRider);
    });

    // STEP 3.4 clone quoteExchange1035 if any
    if (this.quote.quoteExchange1035) {
      newQuote.quoteExchange1035 = new QuoteExchange1035(
        this.quote.quoteExchange1035.cashValueTransfer,
        this.quote.quoteExchange1035.costBasis,
        this.quote.quoteExchange1035.policyType,
        this.quote.quoteExchange1035.applyTowards,
        this.quote.quoteExchange1035.expectedArrival,
        this.quote.quoteExchange1035.isMec,
        newQuote
      );
    }

    // STEP 4 - clone application properties and add to the new app
    this.preserveApplicationProps(this, newApp);

    // STEP 5 - reset certain properties so the application can be shown as "draft" mode in advisor acct center
    this.resetPropertiesForDuplicateApp(newApp);

    // STEP 6 - update insured. This was also done in createClone()
    this.updatePrimaryCustomerData(this, newApp);

    // STEP 7 - add link to source application
    newApp.setSource(sourceType, this.applicationNumber);

    //STEP 8 change new app with options:
    if (duplicateApplicationOptions) {
      newApp.primaryPolicy.insured.person.firstName = duplicateApplicationOptions.firstName
        ? duplicateApplicationOptions.firstName
        : newApp.primaryPolicy.insured.person.firstName;
      newApp.primaryPolicy.insured.person.lastName = duplicateApplicationOptions.lastName
        ? duplicateApplicationOptions.lastName
        : newApp.primaryPolicy.insured.person.lastName;
    }

    //STEP 9 record original application number
    newApp.originalApplicationNumber = this.originalApplicationNumber
      ? this.originalApplicationNumber
      : this.applicationNumber;

    return newApp;
  }

  private cloneQuote(oldQuote: BaseQuote): CoverpathTermQuote | CoverpathWholeLifeQuote {
    let newQuote;
    if (oldQuote.amorphicGetClassName() === CoverpathWholeLifeQuote.amorphicClassName) {
      newQuote = CoverpathWholeLifeQuote.new(this.customer, {
        ...this.getWLQuoteData(oldQuote as CoverpathWholeLifeQuote),
      });
    } else if (oldQuote.amorphicGetClassName() === CoverpathTermQuote.amorphicClassName) {
      newQuote = CoverpathTermQuote.new(this.customer);
      Object.assign(newQuote, { ...this.getTermQuoteData(oldQuote as CoverpathTermQuote) });
    } else {
      // Nothing lives on quote for Annuities
      newQuote = CoverpathAnnuityQuote.new(this.customer);
      Object.assign(newQuote, { ...this.getAnnuityQuoteData(oldQuote as CoverpathAnnuityQuote) });
    }
    return newQuote;
  }

  /**
   *
   * How it's different from cloneApplication (use cases, etc. Can link jira ticket too if you want)
   * This is the same as cloneApplication except we are letting amorphic save the change during postServer call, i.e.
   * - no transient changes
   * - no intermediate transaction
   *
   * @see {@link https://massmutual.atlassian.net/browse/HL-19990|cancel-reapply-issue}
   *
   * Design
   [OUTSIDE] START preServer call should the data refresh
   1. Clone the policies
   2. Clone the app => 2 active apps
   3. Fix [new policy]-to-[new application] reference
   4. Mark old application as prior
   5. Add new application to the applications array
   [OUTSIDE] END postServer call should do the saving
   */
  async cloneApplicationInSession(sourceType: ApplicationSourceType, newProgress?: number): Promise<CasApplication> {
    return super.cloneApplicationInSession(sourceType, newProgress) as Promise<CasApplication>;
  }

  /** Make a copy of a WholeLife quote and only copy over a selected set of the properties
   * These fields are excluded:
   * invite, insurance_quotes_id, application_id, exchange_1035_id
   */
  private getWLQuoteData(quote: CoverpathWholeLifeQuote): Partial<CoverpathWholeLifeQuote> {
    const {
      productType,
      product,
      faceAmount,
      smoker,
      dateOfBirth,
      gender,
      citizenship,
      premium,
      healthClass,
      term,
      contractState,
      waiverOfPremium,
      waiverOfPremiumAmount,
      paymentFrequency,
      isAdvisorQuote,
      ownerEqualsInsured,
      carrierCode,
      isAgentAssistant,
      premiumWithoutECP,
      premiumWithECP,
      bankSale,
      dividend,
      taxBracket,
      loanType,
      flatExtra,
      showPremiumToClient,
    } = quote;

    return {
      productType,
      product,
      faceAmount,
      smoker,
      dateOfBirth,
      gender,
      citizenship,
      premium,
      healthClass,
      term,
      contractState,
      waiverOfPremium,
      waiverOfPremiumAmount,
      paymentFrequency,
      isAdvisorQuote,
      ownerEqualsInsured,
      carrierCode,
      isAgentAssistant,
      premiumWithoutECP,
      premiumWithECP,
      bankSale,
      dividend,
      taxBracket,
      loanType,
      flatExtra,
      showPremiumToClient,
    };
  }

  /** Make a copy of a WholeLife quote and only copy over a selected set of the properties
   * These fields are excluded:
   * invite, insurance_quotes_id, application_id, exchange_1035_id
   */
  private getTermQuoteData(quote: CoverpathTermQuote): Partial<CoverpathTermQuote> {
    const {
      productType,
      product,
      faceAmount,
      smoker,
      dateOfBirth,
      gender,
      citizenship,
      premium,
      healthClass,
      term,
      contractState,
      waiverOfPremium,
      waiverOfPremiumAmount,
      paymentFrequency,
      isAdvisorQuote,
      ownerEqualsInsured,
      carrierCode,
      isAgentAssistant,
      premiumWithoutECP,
      premiumWithECP,
      bankSale,
      healthClassNumber,
    } = quote;

    return {
      productType,
      product,
      faceAmount,
      smoker,
      dateOfBirth,
      gender,
      citizenship,
      premium,
      healthClass,
      term,
      contractState,
      waiverOfPremium,
      waiverOfPremiumAmount,
      paymentFrequency,
      isAdvisorQuote,
      ownerEqualsInsured,
      carrierCode,
      isAgentAssistant,
      premiumWithoutECP,
      premiumWithECP,
      bankSale,
      healthClassNumber,
    };
  }

  /** Make a copy of a WholeLife quote and only copy over a selected set of the properties
   */
  private getAnnuityQuoteData(quote: CoverpathAnnuityQuote): Partial<CoverpathAnnuityQuote> {
    const { productType, product, initialPurchaseAmount, contractState } = quote;

    return {
      productType,
      product,
      initialPurchaseAmount,
      contractState,
    };
  }

  private resetPropertiesForDuplicateApp(application: CasApplication): void {
    application.isAgentAssistant = true;
    // if application is set to 100% (1) change it to 99% (0.99) because they still have to confirm things
    application.progressPercentage = this.progressPercentage === 1 ? 0.99 : this.progressPercentage;

    //get exam vendor from agent rather than policy to assure correct vendor is used for new app
    application.examVendor = this.getExamVendorFromAgent();

    // most properties were already reset during policy cloning, such as policyNumber, submittedAt
    application.primaryPolicy.linkSentDate = null;
    application.primaryPolicy.linkOpenedDate = null;
    application.primaryPolicy.submittedFromIP = null;
    application.primaryPolicy.currentStageRequirements = []; // since it's an array, it can't be null
    application.primaryPolicy.isLiteTouch = null;
    application.lockedBy = null;

    //reset any scheduled exam
    application.primaryPolicy.onlineParamedScheduledDate = null;
    application.primaryPolicy.onlineParamedSchedulingDate = null;
    if (
      application.primaryPolicy.insured &&
      application.primaryPolicy.insured.person &&
      application.primaryPolicy.insured.person.examAddress
    ) {
      application.primaryPolicy.insured.person.examAddress.reset();
    }

    if (application.secondaryPolicy) {
      application.secondaryPolicy.isLiteTouch = null;
    }
  }

  isDraftApplication(hasRegistered: boolean): boolean {
    if (hasRegistered) {
      // when link already sent or link not sent but is not agent assist
      // then it must be either personalized application or generic application
      if (this.primaryPolicy.linkSentDate || !this.isAgentAssistant) {
        if (this.progressPercentage) {
          return false;
        } else {
          // if the user is registered, the link has been sent or the app isn't agent assisted, and there isn't progress
          // on the app, the app is in draft.
          return true;
        }
      } else {
        // if link not sent yet and is agent assist, then must be under agent draft
        return true;
      }
    } else if (this.primaryPolicy.linkOpenedDate) {
      return false;
    } else if (this.primaryPolicy.linkSentDate) {
      if (this.isAgentAssistant && this.linkBeingResent) {
        // if the user isn't registered, but the link has been sent, it is agent assisted and the link is being resent,
        // its a draft
        return true;
      } else {
        return false;
      }
    } else if (this.isAgentAssistant) {
      // if a link is agent assisted, and the user hasn't registed that means it's under agent draft.
      return true;
    }
    // catch all false if none of the conditions are met
    return false;
  }

  public getApplicationType() {
    return CasApplication;
  }

  isDeleted(): boolean {
    return this.type === ApplicationTypes.DELETED;
  }

  setApplicationOriginatorProxy(user: Agent | AgentProxy | OpsProxy, entitlement: Entitlement) {
    this.applicationOriginator.setApplicationOriginator(user, entitlement);
  }

  setApplicationOriginatorProxyId(proxyId, entitlement: Entitlement) {
    this.applicationOriginator.setApplicationOriginatorId(proxyId, entitlement);
  }

  // Who scheduled medical exam
  getExamScheduleOriginator(): ApplicationOriginator {
    return this.actionOriginators.find((originator) => originator.action === OriginatorActions.SCHEDULE_EXAM);
  }

  async getAgentProxy(): Promise<AgentProxy | OpsProxy | Agent> {
    const proxy = await this.applicationOriginator.getAgentProxy();
    return proxy;
  }

  addPolicyFollowers(follower: Agent | AgentProxy | OpsProxy, entitlement: Entitlement): void {
    (this.primaryPolicy as CoverpathPolicy).addPolicyFollower(follower, entitlement);
    if (this.secondaryPolicy) {
      (this.secondaryPolicy as CoverpathPolicy).addPolicyFollower(follower, entitlement);
    }
  }

  async removePolicyFollowers(follower: Agent | AgentProxy | OpsProxy): Promise<void> {
    await (this.primaryPolicy as CoverpathPolicy).removePolicyFollower(follower);
    if (this.secondaryPolicy) {
      await (this.secondaryPolicy as CoverpathPolicy).removePolicyFollower(follower);
    }
  }

  retrieveApplicant() {
    const idMap = {};
    idMap[this._id] = this;
    const origChangeTracking = this.amorphic.__changeTracking__;
    this.amorphic.__changeTracking__ = false;

    UILoggerSingleton.getInstance().info(
      {
        module: 'CasApplication',
        function: 'retrieveApplicant',
        category: UICategory.request,
      },
      `fetching Applicant details`
    );

    return this.primaryPolicy.insured
      .fetch(
        {
          familyHistory: true,
          personalHistory: true,
          riskFactors: true,
        },
        null,
        idMap
      )
      .then(() => (this.amorphic.__changeTracking__ = origChangeTracking));
  }

  retrievePersonalHistory() {
    const idMap = {};
    idMap[this._id] = this;

    // the DB can't handle retrieving all the personal history props
    // in one fetch, so broke this group into 3
    const specSection1 = this.retrievePersonalHistorySection1();
    const specSection2 = this.retrievePersonalHistorySection2();
    const specSection3 = this.retrievePersonalHistorySection3();

    UILoggerSingleton.getInstance().info(
      {
        module: 'CasApplication',
        function: 'retrievePersonalHistory',
        category: UICategory.request,
      },
      'retrieving personal history'
    );

    return Promise.all([
      this.primaryPolicy.insured.fetch(specSection1, null, idMap),
      this.primaryPolicy.insured.fetch(specSection2, null, idMap),
      this.primaryPolicy.insured.fetch(specSection3, null, idMap),
    ]);
  }

  retrievePersonalHistoryBDSpec() {
    return {
      hasBDAnemiaResolvedDiagnosis: true,
      hadBDAnemiaBleedingDiagnosis: true,
      hasBDAnemiaControlledDiagnosis: true,
      hasBDAnemiaSymptomsDiagnosis: true,
      hadBDSpleenDiagnosis: true,
      hadBDClottingDiagnosis: true,
      hadBDAneurysmDiagnosis: true,
      hadBDAutismDetails: true,
      hadBDAnemiaDiagnosis: true,
      hadBDHemochromatosisDiagnosis: true,
      hadMGUSDiagnosis: true,
      hadMyelemoDiagnosis: true,
      hadBloodClotOrCoagulationDefectsDiagnosis: true,
    };
  }

  retrievePersonalHistoryBDMSpec() {
    return {
      hadBMDAmputationsFromTraumaDiagnosis: true,
      hadBMDATypeDiagnosis: true,
      hasBMDMedsDiagnosis: true,
      hasBMDDisabilityBenefitsDiagnosis: true,
      hasBMDOsteoporosisDiagnosis: true,
      hasBMDPagetsDiagnosis: true,
      diagosisBMDAOther: true,
      hasBMDBackPainMedsDiagnosis: true,
    };
  }

  retrievePersonalHistoryCncrSpec() {
    return {
      hadCncrBreastDiagnosis: true,
      hadCncrBrainDiagnosis: true,
      hadCncrColonDiagnosis: true,
      hadCncrOvarianDiagnosis: true,
      hadCncrProstateDiagnosis: true,
      hadCncrRenalDiagnosis: true,
      hadCancerDiagnosis: true,
      hadSkinCncrMelanomaDiagnosis: true,
      hadSkinCncrOtherDiagnosis: true,
      hadLeukemiaDiagnosis: true,
      hadLymphomaDiagnosis: true,
      hadCncrTesticularDiagnosis: true,
    };
  }

  retrievePersonalHistoryCPSpec() {
    return {
      CPDetails: true,
      hadCPPreclampsiaDiagnosis: true,
      hadAbnormalUterineBleedingDiagnosis: true,
      hadCPHELLPDiagnosis: true,
      hadCPHeartLiverKidneyProblemsDiagnosis: true,
      hadCPSeizuresDiagnosis: true,
      hadCPGestationalDiabitiesDiagnosis: true,
    };
  }

  retrievePersonalHistoryDBSpec() {
    return {
      hadDBCerebralPalsyDetails: true,
      hadDBDownSyndromeDetails: true,
      hadDBTumorDiagnosis: true,
      hadDBEncephalititsDiagnosis: true,
      hadDBSeizuresDiagnosis: true,
      hadDBMDisabilityPaymentsDiagnosis: true,
      hadDBMSDiagnosis: true,
      hadDBNeuropathyDiagnosis: true,
      hadDBParkinsonsDiagnosis: true,
      hadDBStrokeDiagnosis: true,
      hadDBFMultipleEpisodesDiagnosis: true,
      hadDBFLastYearDiagnosis: true,
      hadDBTIADiagnosis: true,
      hadDBHematomaDiagnosis: true,
      DiagnosisisDTNotSure: true,
      diabetesDiagnosis: true,
    };
  }

  retrievePersonalHistoryDDSpec() {
    return {
      hadDDCeliacDiseaseDiagnosis: true,
      hadDDColonPolypDiagnosis: true,
      hadDDChronsDiagnosis: true,
      hadDDColitusNonUlcerativeDiagnosis: true,
      hadDDColitusUlcerativeDiagnosis: true,
      hadDDDLast6MonthsDiagnosis: true,
      hadDDNAFLDDiagnosis: true,
      hadDDHepititisDiagnosis: true,
      hadDDPancreaticInflamationDiagnosis: true,
      hadDDPancreatitisDiagnosis: true,
      hadDDURecoveredDiagnosis: true,
      hadDDWeightLossSurgeryDiagnosis: true,
      hadDDAnorectalFistulaDiagnosis: true,
      hadDDGastritisDiagnosis: true,
      hadDDBenignDiagnosis: true,
      diagnosisDDOther: true,
    };
  }

  retrievePersonalHistoryECGSpec() {
    return {
      hadECGNormalDiagnosis: true,
    };
  }

  retrievePersonalHistoryEENTSpec() {
    return {
      hadEENTAcousicNeuromaDiagnosis: true,
      hadEENTLeukoplakiaDiagnosis: true,
      hadEENTOpticNeuritisDiagnosis: true,
    };
  }

  retrievePersonalHistoryEOSpec() {
    return {
      hadEOHospitalizedLast5YearsDiagnosis: true,
      hadEOAlcoholismDiagnosis: true,
      hadEOADepressionDiagnosis: true,
      hadEODiagnosis: true,
      hadEODelusionDiagnosis: true,
      hadEODrugCounselingDiagnosis: true,
      hadEOEatingDisorderDiagnosis: true,
      hadEOEatingDisorderHospitalizedDiagnosis: true,
      hadEOMajorDepressionDiagnosis: true,
      hadEOPAMedsLast2YearsDiagnosis: true,
      hadEOSuicideAttemptDiagnosis: true,
      hadChangeDoseEOATreatmentDiagnosis: true,
      hadEOPsychosisHospitalizedDiagnosis: true,
      hadEOADepressionOther: true,
      hadEODepressionSocialSituationalDiagnosis: true,
      hadEODelusionHospitalizedLast10YearsDiagnosis: true,
      hadEOPTSDHospitalizedLast10YearsDiagnosis: true,
      hadEOOCDHospitalizedLast10YearsDiagnosis: true,
      hadEOBipolarHospitalizedDiagnosis: true,
      diagnosisEONotSure: true,
      hadAlcoholAbuseDiagnosis: true,
      hadEOADepressionHospitalizedDiagnosis: true,
    };
  }

  retrievePersonalHistoryHCSpec() {
    return {
      hadHCAttackDiagnosis: true,
      hadHCAbnormalBeatDiagnosis: true,
      hadHCHMedicationDiagnosis: true,
      hadHCMurmurDiagnosis: true,
      hadHCPericarditisDiagnosis: true,
      hadHCAorticAneurysmDiagnosis: true,
      hadHCPeripheralArteryVeinDiseaseDiagnosis: true,
      hadHCChestPainsDiagnosis: true,
    };
  }

  retrievePersonalHistoryIDSpec() {
    return {
      hadIDOBenignDiagnosis: true,
      hadIDPolycysticDiagnosis: true,
      DiagnosisIDNotSure: true,
      hadIDCervicitisDiagnosisDiagnosis: true,
    };
  }

  retrievePersonalHistoryRDSpec() {
    return {
      hadRDCOPDDiagnosis: true,
      hadRDEmphysemaDiagnosis: true,
      hadRDAHospitalizedLast1YearDiagnosis: true,
      hadRDAERLast1YearDiagnosis: true,
      hasRDAMedsDiagnosis: true,
      hadRDPulmonaryNoduleDiagnosis: true,
      hasRDApneaDiagnosis: true,
      hasRDPRecoveredDiagnosis: true,
      hadRDTuberculosisDiagnosis: true,
      hadRDSarcoidosisDiagnosis: true,
      hadRDChronicFatigueDiagnosis: true,
      hadRDEBOtherSymptomsDiagnosis: true,
      hadRDLupusDiagnosis: true,
      hadRDLymeDiseaseDetails: true,
      hasRDBRecoveredDiagnosis: true,
      diagnosisRDOther: true,
      diagnosisRMDOther: true,
      hasHypothyroidDiagnosis: true,
      hasDTProlactinomaDiagnosis: true,
      hasDTTImageBiopsyDiagnosis: true,
      hasDTTStableDiagnosis: true,
    };
  }

  retrievePersonalHistorySDSpec() {
    return {
      DiagnosisSDNotSure: true,
      hadSDArthritisDiagnosis: true,
      hasSDMedsDiagnosis: true,
    };
  }

  retrievePersonalHistoryTBSpec() {
    return {
      hasTBISymptomsOrMedsDiagnosis: true,
      diagnosisTB: true,
    };
  }

  retrievePersonalHistoryUTDSpec() {
    return {
      hadUTDGlomerulonephiritisDiagnosis: true,
      hasUTDNormalKidneyDiagnosis: true,
      hadUTDRenalFailureDiagnosis: true,
      hadUTDProteinuriaDiagnosis: true,
      hadUTDRCMultipleDiagnosis: true,
      hadUTStructuralDiagnosis: true,
      hadUTDElevatedPSADiagnosis: true,
      hadUTDInterstitialCystitisDiagnosis: true,
      DiagnosisUTDNotSure: true,
      hadUrninaryTrackDisorderDiagnosis: true,
    };
  }

  retrievePersonalHistoryNephritisSpec() {
    return {
      hadNephritisLast2YearsDiagnosis: true,
      hadNephritisMoreThanLast2YearsDiagnosis: true,
    };
  }

  retrievePersonalHistoryHIVSpec() {
    return {
      hadHIVTreatmentAdviceDiagnosis: true,
      hivNegativeAddress: true,
      hadImmunoDeficiencyDiagnosis: true,
      hadFullTestExclHIVDiagnosis: true,
    };
  }

  retrievePersonalHistoryMiscSpec() {
    return {
      primaryPhysicianPerson: true,
      hadTreatmentOtherDiagnosis: true,
      hadNotCompletedTestDiagnosis: true,
      disabledDetails: true,
    };
  }

  retrievePersonalHistorySection1() {
    return {
      personalHistory: {
        fetch: {
          ...this.retrievePersonalHistoryBDSpec(),
          ...this.retrievePersonalHistoryBDMSpec(),
          ...this.retrievePersonalHistoryCncrSpec(),
          ...this.retrievePersonalHistoryCPSpec(),
          ...this.retrievePersonalHistoryDBSpec(),
          ...this.retrievePersonalHistoryDDSpec(),
        },
      },
    };
  }

  retrievePersonalHistorySection2() {
    return {
      personalHistory: {
        fetch: {
          ...this.retrievePersonalHistoryECGSpec(),
          ...this.retrievePersonalHistoryEENTSpec(),
          ...this.retrievePersonalHistoryEOSpec(),
          ...this.retrievePersonalHistoryHCSpec(),
          ...this.retrievePersonalHistoryIDSpec(),
        },
      },
    };
  }

  retrievePersonalHistorySection3() {
    return {
      personalHistory: {
        fetch: {
          ...this.retrievePersonalHistoryRDSpec(),
          ...this.retrievePersonalHistorySDSpec(),
          ...this.retrievePersonalHistoryTBSpec(),
          ...this.retrievePersonalHistoryUTDSpec(),
          ...this.retrievePersonalHistoryNephritisSpec(),
          ...this.retrievePersonalHistoryHIVSpec(),
          ...this.retrievePersonalHistoryMiscSpec(),
        },
      },
    };
  }

  get primaryPolicyWithPendingAmendment(): CoverpathPolicy {
    if (this.isJointApp()) {
      if (this.primaryPolicy.typeWithPendingAmendment() === POLICY_TYPES.AMEND_REMOVE_PRIMARY) {
        return this.secondaryPolicy;
      } else {
        return this.primaryPolicy;
      }
    } else {
      if (this.wholeLifePolicy && this.wholeLifePolicy.typeWithPendingAmendment() === POLICY_TYPES.PRIMARY) {
        return this.wholeLifePolicy as CoverpathPolicy;
      } else {
        return this.primaryPolicy;
      }
    }
  }

  static isJointApp(primaryPolicy: Policy, secondaryPolicy: Policy): boolean {
    return primaryPolicy && primaryPolicy.isWholeLifePolicy() && secondaryPolicy && secondaryPolicy.isTermLifePolicy();
  }

  isJointApp(): boolean {
    const primaryPolicy = this.primaryPolicy;
    const secondaryPolicy = this.secondaryPolicy;

    return !!CasApplication.isJointApp(primaryPolicy, secondaryPolicy);
  }

  isJointAppWithPendingAmendment(): boolean {
    const primaryPolicy = this.primaryPolicyWithPendingAmendment;
    const secondaryPolicy = this.secondaryPolicyWithPendingAmendment;
    return !!CasApplication.isJointApp(primaryPolicy, secondaryPolicy);
  }

  wasJointAppBeforeAmendment(): boolean {
    return this.primaryPolicy && !!this.getSecondaryPolicyOfAnyAmendmentStatus();
  }

  private jointAppAdditionHelper(fieldName: string): number | null {
    const primary = this.primaryPolicy;

    if (this.isJointApp()) {
      const secondary = this.secondaryPolicy;

      if (primary && Number.isFinite(primary[fieldName]) && secondary && Number.isFinite(secondary[fieldName])) {
        return primary[fieldName] + secondary[fieldName];
      } else {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'Policy',
            function: 'jointAppAdditionHelper',
            category: UICategory.request,
          },
          `Cannot determine total ${fieldName} - both primary and secondary policies must have a value`
        );

        return null;
      }
    } else {
      return primary[fieldName];
    }
  }

  getTotalFaceAmount(): number | null {
    return this.jointAppAdditionHelper('faceAmount');
  }

  getTotalWaiverPremiumForRiders(): number {
    let amount = this.primaryPolicy.getTotalWaiverPremiumForRiders();
    if (this.isJointApp()) {
      amount += this.secondaryPolicy.getTotalWaiverPremiumForRiders();
    }
    return amount;
  }

  getTotalWaiverPremiumForRidersWithPendingAmendment(): number {
    let amount = this.primaryPolicyWithPendingAmendment.getTotalWaiverPremiumForRidersWithPendingAmendment();
    if (this.isJointAppWithPendingAmendment()) {
      amount += this.secondaryPolicyWithPendingAmendment.getTotalWaiverPremiumForRidersWithPendingAmendment();
    }
    return amount;
  }

  getTotalPremiumWithPendingAmendment(): number {
    let amount = this.primaryPolicyWithPendingAmendment.premiumBreakdown.getTotalWithPendingAmendment();
    if (this.isJointAppWithPendingAmendment()) {
      amount += this.secondaryPolicyWithPendingAmendment.premiumBreakdown.getTotalWithPendingAmendment();
    }
    return amount;
  }

  getTotalFaceAmountWithPendingAmendment(): number {
    let totalFaceAmount = this.primaryPolicyWithPendingAmendment.faceAmountWithPendingAmendment();
    if (this.isJointAppWithPendingAmendment()) {
      totalFaceAmount += this.secondaryPolicyWithPendingAmendment.faceAmountWithPendingAmendment();
    }
    return totalFaceAmount;
  }

  get secondaryPolicyWithPendingAmendment(): CoverpathPolicy {
    if (this.secondaryPolicy) {
      if (this.secondaryPolicy.typeWithPendingAmendment() !== POLICY_TYPES.AMEND_REMOVE_SECONDARY) {
        return this.secondaryPolicy as CoverpathPolicy;
      }
    }

    return (this.policies || []).reduce((secondaryPolicy, pol) => {
      return secondaryPolicy || (pol.typeWithPendingAmendment() === POLICY_TYPES.SECONDARY ? pol : null);
    }, null) as CoverpathPolicy | null;
  }

  get previouslyAddedSecondaryPolicy(): CoverpathPolicy {
    return (this.policies || []).reduce((secondaryPolicy, pol) => {
      return secondaryPolicy || (pol.typeWithPendingAmendment() === POLICY_TYPES.AMEND_REMOVE_ADDED ? pol : null);
    }, null) as CoverpathPolicy | null;
  }

  get primaryPolicy(): CoverpathPolicy {
    return (this.policies || []).reduce((priPol, pol) => {
      return priPol || (pol.isPrimary ? pol : null);
    }, null);
  }

  get secondaryPolicy(): CoverpathPolicy {
    return _.find(this.policies, (policy) => {
      return policy.type === POLICY_TYPES.SECONDARY;
    });
  }

  get termPolicy(): Policy {
    return this.policies.find((pol) => Product.products[pol.product].type === Product.productTypes.Term);
  }

  get wholeLifePolicy(): CoverpathPolicy {
    return this.policies.find((pol) => Product.products[pol.product].type === Product.productTypes.Whole);
  }

  async createClonedApplicationAsync(): Promise<Application> {
    const newApp = await super.createClonedApplicationAsync();
    this.updateQuote(this, newApp);
    return newApp;
  }

  /**
   * Same as createClonedApplicationAsync, except
   * - We don't want to make the fetch call because it overides non-peristed changes, so we call "createClonedPolicy" instead of "fetchAndCreateClonedPolicy"
   * - We are not marking oldApplication as prior
   * @see {@link https://massmutual.atlassian.net/browse/HL-19990|cancel-reapply-issue}
   */
  async createClone(sourceType?: ApplicationSourceType): Promise<CasApplication> {
    return super.createClone(sourceType) as Promise<CasApplication>;
  }

  hasAnyInForce(): boolean {
    if (!this.isJointApp()) {
      return super.hasAnyInForce();
    } else {
      return this.primaryPolicy.isInForce() || this.secondaryPolicy.isInForce();
    }
  }

  hasOnlyCanceled(): boolean {
    if (!this.isJointApp()) {
      return super.hasOnlyCanceled();
    } else {
      return this.primaryPolicy.hasEnded() && this.secondaryPolicy.hasEnded();
    }
  }

  getTotalPremium(): number {
    let amount = super.getTotalPremium();
    if (this.isJointApp()) {
      amount += this.secondaryPolicy.premiumBreakdown.getTotal();
    }
    return amount;
  }

  getTotalTlicPremium(): number {
    let amount = super.getTotalTlicPremium();
    if (this.isJointApp() && this.secondaryPolicy.tLICPremiumBreakdown) {
      amount += this.secondaryPolicy.tLICPremiumBreakdown.getTotal();
    }

    return amount;
  }

  getPolicyRemovedByAmendment() {
    return _.find(this.policies, (policy) => {
      return policy.type === POLICY_TYPES.AMEND_REMOVE_PRIMARY || policy.type === POLICY_TYPES.AMEND_REMOVE_SECONDARY;
    });
  }

  getSecondaryPolicyOfAnyAmendmentStatus() {
    return _.find(this.policies, (policy) => {
      return (
        policy.type === POLICY_TYPES.SECONDARY ||
        policy.type === POLICY_TYPES.AMEND_REMOVE_SECONDARY ||
        policy.type === POLICY_TYPES.AMEND_REMOVE_PRIMARY
      );
    });
  }

  isActive(): boolean {
    // TODO: use application.type === ApplicationTypes.ACTIVE after this MR is merged:
    // https://app.gitlab.havenlife.com/hit/haven/-/merge_requests/8650
    return !this.type || this.type === 'active';
  }

  isAgentSubmittedAppTrigger() {
    if (this.isAgentSubmittedApp) {
      this.tlicOptedIn = false;
      this.manualExam = false;
      this.isLockedByAdvisor = false;
    }
  }
}
